import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { ptBR } from '@mui/x-date-pickers/locales';
import { toZonedTime } from 'date-fns-tz';
import moment, { Moment } from 'moment';
import 'moment-timezone';
import 'moment/dist/locale/pt-br';

import React, { useEffect, useState } from 'react';
import { JAZZ_TIME_ZONE } from 'utils/jazzTimeZone';
import { useStoreSelector } from 'v3/data/store';
import { Content, DatePickerStyled, slotProps } from './styles';
import { CustomDatePickerProps } from './types';

moment.tz.setDefault(JAZZ_TIME_ZONE);
moment.locale('pt-br');

export const DateRangePicker: React.FC<CustomDatePickerProps> = ({
  value = [null, null],
  defaultValue = [null, null],
  shouldDisableDate = () => false,
  disablePast = false,
  disableFuture = true,
  onChangeDatePicker,
  minDate,
  maxDate,
  label,
}) => {
  const { filter } = useStoreSelector((store) => store.rankSeller);

  const [startDate, setStartDate] = useState<Moment | null>(moment(filter.startDate) || defaultValue[0]);
  const [endDate, setEndDate] = useState<Moment | null>(moment(filter.finalDate) || defaultValue[1]);
  const [openStart, setOpenStart] = useState(false);
  const [openEnd, setOpenEnd] = useState(false);

  useEffect(() => {
    setStartDate(value[0]);
    setEndDate(value[1]);
  }, [value]);

  const handleStartDateChange = (date: unknown) => {
    if (!date) return;
    const dateMoment = moment(date as Moment);
    const zonedDate = moment(toZonedTime(dateMoment.toDate(), JAZZ_TIME_ZONE));

    if (endDate && zonedDate.isAfter(endDate, 'day')) {
      setEndDate(null);
    }
    setStartDate(zonedDate);
  };

  const handleEndDateChange = (date: unknown) => {
    if (!date) return;
    const dateMoment = moment(date as Moment);
    const zonedDate = moment(toZonedTime(dateMoment.toDate(), JAZZ_TIME_ZONE));

    if (startDate && zonedDate.isBefore(startDate, 'day')) {
      setEndDate(startDate);
      setStartDate(zonedDate);
    } else {
      setEndDate(zonedDate);
    }
    setOpenEnd(false);
  };

  useEffect(() => {
    onChangeDatePicker([startDate ? startDate.format() : null, endDate ? endDate.format() : null]);
  }, [startDate, endDate]);

  const shouldDisableStartDate = (date: unknown) => {
    const dateMoment = moment(date as Moment);
    return shouldDisableDate(dateMoment);
  };

  const shouldDisableEndDate = (date: unknown) => {
    const dateMoment = moment(date as Moment);
    if (shouldDisableDate(dateMoment)) return true;
    if (startDate && dateMoment.isBefore(startDate, 'day')) return true;
    if (maxDate && dateMoment.isAfter(maxDate, 'day')) return true;
    return false;
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterMoment}
      adapterLocale="pt-br"
      localeText={ptBR.components.MuiLocalizationProvider.defaultProps.localeText}
    >
      <Content>
        <DatePickerStyled
          label={label ? `${label} - Início` : 'Data Inicial'}
          value={startDate}
          onChange={handleStartDateChange}
          format="DD/MM/YYYY"
          open={openStart}
          onOpen={() => setOpenStart(true)}
          onClose={() => setOpenStart(false)}
          desktopModeMediaQuery="theme.breakpoints.up('md')"
          views={['year', 'month', 'day']}
          slots={{ openPickerIcon: CalendarTodayIcon }}
          slotProps={{
            ...slotProps,
            openPickerButton: {
              sx: { display: 'none' },
            },
            textField: {
              ...slotProps?.textField,
              onClick: () => setOpenStart(true),
            },
          }}
          disablePast={false}
          disableFuture={disableFuture}
          shouldDisableDate={shouldDisableStartDate}
          minDate={minDate}
          maxDate={maxDate}
          orientation="portrait"
          localeText={{ toolbarTitle: 'Escolha a data inicial', cancelButtonLabel: 'Cancelar' }}
        />
        <DatePickerStyled
          label={label ? `${label} - Fim` : 'Data Final'}
          format="DD/MM/YYYY"
          value={endDate}
          onChange={handleEndDateChange}
          open={openEnd}
          onOpen={() => setOpenEnd(true)}
          onClose={() => setOpenEnd(false)}
          desktopModeMediaQuery="theme.breakpoints.up('md')"
          views={['year', 'month', 'day']}
          slots={{ openPickerIcon: CalendarTodayIcon }}
          slotProps={{
            ...slotProps,
            openPickerButton: {
              sx: { display: 'none' },
            },
            textField: {
              ...slotProps?.textField,
              onClick: () => setOpenEnd(true),
            },
          }}
          disablePast={disablePast}
          disableFuture={disableFuture}
          shouldDisableDate={shouldDisableEndDate}
          minDate={startDate || minDate}
          maxDate={maxDate}
          orientation="portrait"
          localeText={{ toolbarTitle: 'Escolha a data final', cancelButtonLabel: 'Cancelar' }}
        />
      </Content>
    </LocalizationProvider>
  );
};
